import { useMemo, useState } from 'react';
import type { Command } from '@integrate/contracts';
import {
  COLUMN_FIELDS,
  ORG_HEAD_GROUP,
  computeDiagnostics,
  currentBucket,
  fmt,
  getHeadFor,
  groupNodeId,
  mappingConfidence,
  resolveField,
  type ColumnKey,
  type GroupingLevel,
  type MappingConfidence,
  type SeparationState,
} from '@integrate/core';
import { Modal } from '../ui/Modal.js';
import { StatTile } from '../ui/StatTile.js';
import { ICON, IconButton } from '../ui/Icon.js';
import { Section } from './sections.js';

/**
 * The Data phase panel, ported from `p1PanelHtml` (orgchart.html:5999).
 *
 * Four questions, in the order the app asks them: what did we load and how is it mapped, how much
 * dedication data is there (because that is what the perimeter rule runs on), what is wrong with the
 * data, and who has a designated head. Then the optional Target phase toggle, which is the one control
 * on this panel that is a command rather than a reading.
 */
export function DataPanel({
  state,
  readOnly,
  onDispatch,
  onOpenDiagnostics,
  onOpenMapping,
  onOpenRefresh,
  onOpenAssumptions,
  onNext,
}: {
  state: SeparationState;
  readOnly: boolean;
  onDispatch(command: Command): void;
  onOpenDiagnostics(): void;
  onOpenMapping(): void;
  /** The mid-engagement roster refresh. Its own screen, because it is its own decision. */
  onOpenRefresh(): void;
  onOpenAssumptions(): void;
  onNext(): void;
}): JSX.Element {
  const m = state.columnMap;
  const all = state.employees;

  const diagnostics = useMemo(() => computeDiagnostics(state), [state]);

  const withDedication = all.filter((e) => e.dedication != null).length;
  const coverage = all.length ? Math.round((withDedication / all.length) * 100) : 0;
  const optional = COLUMN_FIELDS.filter((c) => !c.required && m[c.key]).length;
  const optionalTotal = COLUMN_FIELDS.filter((c) => !c.required).length;

  // Heads coverage at the first hierarchy level, plus the whole-organisation head.
  const lvl1 = state.groupingLevels[0];
  let headsLine = 'No hierarchy levels configured.';
  if (lvl1) {
    const bucketOf = (id: string): string => currentBucket(state.design, id);
    const values = [
      ...new Set(
        all
          .map((e) => resolveField(state, e, lvl1.key, 'as-is'))
          .filter((v) => v != null && String(v).trim() !== ''),
      ),
    ];
    const withHead = values.filter((v) =>
      getHeadFor(state, groupNodeId([{ levelKey: lvl1.key, value: String(v) }]), 'as-is', bucketOf),
    ).length;
    const orgHead = getHeadFor(state, ORG_HEAD_GROUP, 'as-is', bucketOf);
    headsLine = `${withHead}/${values.length} ${lvl1.label} groups have a designated head · organisation head ${
      orgHead ? '★ set' : 'not set'
    }`;
  }

  return (
    <>
      <Section
        testId="data-mapping"
        /* A plain `&` now that this is a string attribute rather than JSX text. Both render the same
           — JSX decodes entities in attribute values too — but only one reads like a title. */
        title="Import & mapping"
      >
        <Row k="People loaded" v={fmt(all.length)} testId="data-people" />
        <Row k="Hierarchy levels" v={String(state.groupingLevels.length)} />
        <Row k="Optional columns mapped" v={`${optional}/${optionalTotal}`} />
        <Row k="Dedication column" v={m.dedication ? m.dedication : '— not mapped'} />
        <button style={{ width: '100%', marginTop: 8 }} data-testid="open-mapping" onClick={onOpenMapping}>
          Review column mapping…
        </button>
        {/*
          Beside the mapping rather than in its own phase: a refresh is read under the CURRENT mapping,
          and the server refuses a run staged under a different one. Somebody about to refresh should
          have the mapping in view, because changing it is the thing that invalidates a staged file.
        */}
        <button style={{ width: '100%', marginTop: 8 }} data-testid="open-refresh" onClick={onOpenRefresh}>
          Refresh the roster…
        </button>
      </Section>

      <Section
        testId="data-dedication"
        title={
          <>
            Dedication coverage <span className="pill">{coverage}%</span>
          </>
        }
      >
        <p className="muted">
          Dedication (0–100% of each person&rsquo;s work serving the carve-out) drives the perimeter
          threshold in phase 2.
        </p>
        <div className="stat-grid">
          <StatTile label="With dedication" value={fmt(withDedication)} sub={`${coverage}% of people`} />
          <StatTile
            label="Missing"
            value={fmt(all.length - withDedication)}
            sub="manual designation"
          />
        </div>
        {!m.dedication && !withDedication && (
          <p className="muted">
            No dedication data: map a column (or enter values per person) to enable the threshold rule —
            or build the perimeter manually in phase 2.
          </p>
        )}
      </Section>

      <Section
        testId="data-quality"
        title={
          <>
            Data quality{' '}
            <span className="pill">
              {diagnostics.errors.length + diagnostics.warnings.length || '✓'}
            </span>
          </>
        }
      >
        <Row k="Errors" v={String(diagnostics.errors.length)} testId="diag-errors" />
        <Row k="Warnings" v={String(diagnostics.warnings.length)} testId="diag-warnings" />
        <Row k="Assumptions applied" v={String(diagnostics.assumptions.length)} />
        <button style={{ width: '100%', marginTop: 8 }} data-testid="open-diagnostics" onClick={onOpenDiagnostics}>
          Open diagnostics…
        </button>
      </Section>

      {/*
        Named "Cost assumptions", not "Assumptions": the row above uses that word for the data
        normalisations the importer applied, and somebody sent from another panel to "see Assumptions"
        would otherwise land on a list of coerced salary strings.
      */}
      <Section
        testId="data-assumptions"
        title="Cost assumptions"
      >
        <Row k="Loaded cost" v={`salary × ${state.assumptions.loadedCostMultiplier}`} />
        <Row k="Default severance" v={`${fmt(state.assumptions.severanceMonthsDefault, 1)} months`} />
        <Row k="Hiring cost" v={`${state.assumptions.hiringCostPct}% of salary`} />
        <p className="muted">
          Behind every cost figure in the tool, and global across every scenario. Override severance per
          country or grade where redundancy regimes differ.
        </p>
        <button style={{ width: '100%' }} data-testid="open-assumptions" onClick={onOpenAssumptions}>
          Edit cost assumptions…
        </button>
      </Section>

      <Section
        testId="data-heads"
        title="Heads"
      >
        <p className="muted">{headsLine}</p>
        <p className="muted">Designate heads from a person&rsquo;s panel.</p>
      </Section>

      <Section
        testId="data-target"
        title={
          <>
            Target org design (optional) <span className="pill">{state.targetEnabled ? 'on' : 'off'}</span>
          </>
        }
      >
        <p className="muted">
          Add an optional stage to design the carve-out&rsquo;s future-state org — build the target units,
          then place people into them at Perimeter / Day 1.
        </p>
        <label className="check">
          <input
            type="checkbox"
            data-testid="target-toggle"
            checked={state.targetEnabled}
            disabled={readOnly}
            onChange={(e) => onDispatch({ type: 'setTargetEnabled', enabled: e.target.checked })}
          />
          <span>Enable the Target org design phase</span>
        </label>
      </Section>

      <button className="primary" style={{ width: '100%' }} data-testid="data-next" onClick={onNext}>
        Next: {state.targetEnabled ? 'design the target org' : 'build the perimeter'} →
      </button>
    </>
  );
}

/* ------------------------------------------------------------------ */

function Row({ k, v, testId }: { k: string; v: string; testId?: string }): JSX.Element {
  return (
    <div className="sp-row" data-testid={testId}>
      <span className="k">{k}</span>
      <span className="v">{v}</span>
    </div>
  );
}

/**
 * The column-mapping editor.
 *
 * Deliberately a draft with an explicit Apply, which the shipped app's modal is not: there, every
 * keystroke writes live state, Cancel reverts nothing, and re-confirming the mapping rebuilds the roster
 * from the original upload — deleting every scenario, head, build-up and the whole change log with no
 * confirmation. Here the mapping is a command like any other, it re-derives fields from the retained
 * source rows without touching the design, and the destructive rebuild is a separate, named command.
 */
const CONFIDENCE_LABEL: Record<MappingConfidence, string> = {
  high: 'High',
  likely: 'Likely',
  possible: 'Possible',
  manual: 'Manual',
  none: '',
};

/*
  The column mapping, and the hierarchy declared through it.

  BOTH IN ONE DIALOG, deliberately, because they are one decision. A grouping level IS a mapped
  column — `setGroupingLevels` refuses a level whose key has no column, and `setColumnMapping`
  refuses to orphan a declared level — so the two cannot be edited on separate screens without
  producing states neither command will accept. The frozen app puts them on one modal for the same
  reason (orgchart.html:2800), and this follows it.

  THE ORDER OF THE TWO COMMANDS IS LOAD-BEARING and is computed on apply, not fixed:
    · declaring a level needs its column mapped FIRST, so mapping precedes levels;
    · dropping a column needs the level undeclared FIRST, so levels precede mapping.
  Sending them in the wrong order does not corrupt anything — the second command refuses — but it
  refuses with a message about a state the user did not ask for, which is worse than useless.

  CONFIDENCE IS SHOWN because a mapping is now GUESSED on a plain client extract. A field matched on
  a header that merely contains a known spelling is the one that quietly mis-reads a column, and the
  only moment anyone will look is this one.
*/
export function MappingEditor({
  state,
  projectName,
  readOnly,
  canRebuild,
  onApply,
  onClose,
}: {
  state: SeparationState;
  /** For the re-derive confirmation, which asks for it by name. Not on SeparationState. */
  projectName: string;
  readOnly: boolean;
  /** Owner-only, and pointless without retained source rows. */
  canRebuild: boolean;
  onApply(commands: Command[]): void;
  onClose(): void;
}): JSX.Element {
  const [draft, setDraft] = useState<Record<string, string>>(() => ({ ...state.columnMap }));
  const [draftLevels, setDraftLevels] = useState<GroupingLevel[]>(() => [...state.groupingLevels]);
  const [confirmName, setConfirmName] = useState('');

  const columns = state.excelColumns;
  const levels = draftLevels;
  const levelsDirty = useMemo(
    () =>
      draftLevels.length !== state.groupingLevels.length ||
      draftLevels.some(
        (l, i) => l.key !== state.groupingLevels[i]?.key || l.label !== state.groupingLevels[i]?.label,
      ),
    [draftLevels, state.groupingLevels],
  );
  const dirty = useMemo(() => {
    const keys = new Set([...Object.keys(draft), ...Object.keys(state.columnMap)]);
    return levelsDirty || [...keys].some((k) => (draft[k] ?? '') !== (state.columnMap[k] ?? ''));
  }, [draft, state.columnMap, levelsDirty]);

  /*
    The next level key, which may not reuse a retired one.

    `setGroupingLevels` refuses any `lvlN` at or below the highest ever issued, because a reused key
    silently inherits the removed level's heads and per-person overrides. The highest is computed
    over the DECLARED levels and every key the column map still mentions, so a level removed earlier
    in this same dialog cannot be handed back out.
  */
  const nextLevelKey = (): string => {
    let highest = 0;
    const consider = (key: string): void => {
      const m = /^lvl(\d+)$/.exec(key);
      if (m) highest = Math.max(highest, Number(m[1]));
    };
    state.groupingLevels.forEach((l) => consider(l.key));
    draftLevels.forEach((l) => consider(l.key));
    Object.keys(state.columnMap).forEach(consider);
    Object.keys(draft).forEach(consider);
    return `lvl${highest + 1}`;
  };

  const addLevel = (): void => {
    const key = nextLevelKey();
    setDraftLevels((ls) => [...ls, { key, label: `Level ${ls.length + 1}` }]);
  };

  const removeLevel = (key: string): void => {
    setDraftLevels((ls) => ls.filter((l) => l.key !== key));
    setDraft((d) => {
      const next = { ...d };
      delete next[key];
      return next;
    });
  };

  const moveLevel = (index: number, delta: number): void =>
    setDraftLevels((ls) => {
      const to = index + delta;
      if (to < 0 || to >= ls.length) return ls;
      const next = [...ls];
      const [moved] = next.splice(index, 1);
      next.splice(to, 0, moved!);
      return next;
    });

  const relabel = (key: string, label: string): void =>
    setDraftLevels((ls) => ls.map((l) => (l.key === key ? { ...l, label } : l)));

  const set = (key: string, value: string): void =>
    setDraft((d) => {
      const next = { ...d };
      // An unmapped field is an ABSENT key, not an empty string: `if (!m.salary)` is how every consumer
      // asks the question, and `''` would answer it the same way while still serialising as a mapping.
      if (value) next[key] = value;
      else delete next[key];
      return next;
    });

  const field = (key: string, label: string, required: boolean, confidence?: ColumnKey): JSX.Element => {
    const level = confidence ? mappingConfidence(confidence, draft) : 'none';
    return (
      <div className="field" key={key}>
        <label htmlFor={`map-${key}`}>
          {label}
          {required && <span className="req"> required</span>}
          {level !== 'none' && (
            <span
              className={`pill conf-${level}`}
              data-testid={`conf-${key}`}
              title={
                level === 'high'
                  ? 'The column name matches this field exactly.'
                  : level === 'likely'
                    ? 'The column name starts or ends with a known spelling of this field.'
                    : level === 'possible'
                      ? 'The column name only contains a known spelling — worth checking.'
                      : 'Chosen by hand: the column name says nothing about this field.'
              }
            >
              {CONFIDENCE_LABEL[level]}
            </span>
          )}
        </label>
        <select
          id={`map-${key}`}
          data-testid={`map-${key}`}
          value={draft[key] ?? ''}
          disabled={readOnly}
          onChange={(e) => set(key, e.target.value)}
        >
          <option value="">— not mapped —</option>
          {columns.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>
    );
  };

  const missingRequired = COLUMN_FIELDS.filter((c) => c.required && !draft[c.key]).map((c) => c.label);
  const levelsWithoutColumn = levels.filter((l) => !draft[l.key]).map((l) => l.label);

  /*
    The commands an apply becomes, in the order they must land.

    A level being ADDED needs its column mapped before it can be declared; a level being REMOVED
    needs to be undeclared before its column can go. When both happen at once, mapping first would
    orphan nothing (the removed level is still declared but keeps its column until the second
    command) — so mapping-then-levels is safe in every case EXCEPT a pure removal, where the column
    drop must wait. Splitting on that is what the two branches below are.
  */
  const commandsForApply = (): Command[] => {
    const mapping: Command = { type: 'setColumnMapping', columnMap: draft };
    const declare: Command = { type: 'setGroupingLevels', levels };
    if (!levelsDirty) return [mapping];

    const removed = state.groupingLevels.some((l) => !levels.some((d) => d.key === l.key));
    // A removal has to be declared before the column it used can be dropped.
    return removed ? [declare, mapping] : [mapping, declare];
  };

  return (
    <Modal
      testId="mapping-modal"
      title="Column mapping"
      onClose={onClose}
      footer={
        <>
          {missingRequired.length > 0 && (
            <span className="muted" data-testid="mapping-missing">
              Still unmapped: {missingRequired.join(', ')}
            </span>
          )}
          {missingRequired.length === 0 && levelsWithoutColumn.length > 0 && (
            <span className="muted" data-testid="levels-missing">
              Pick a column for: {levelsWithoutColumn.join(', ')}
            </span>
          )}
          <span className="spacer" />
          <button onClick={onClose}>Cancel</button>
          <button
            className="primary"
            data-testid="mapping-apply"
            disabled={
              readOnly || !dirty || missingRequired.length > 0 || levelsWithoutColumn.length > 0
            }
            onClick={() => {
              onApply(commandsForApply());
              onClose();
            }}
          >
            Apply mapping
          </button>
        </>
      }
    >
      {columns.length === 0 ? (
        <p className="muted" data-testid="mapping-no-columns">
          This project did not retain its source column names, so there is nothing to remap. Import
          the extract again if the mapping needs to change.
        </p>
      ) : (
        <>
          <p className="muted">
            Which spreadsheet column each field is read from. Changing this does not move anybody:
            the design — scenarios, decisions, heads, build-ups and the change log — is untouched.
            To re-read the people themselves under a corrected mapping, use “Re-derive the roster”
            below.
          </p>
          {COLUMN_FIELDS.map((c) => field(c.key, c.label, c.required, c.key))}

          <h5 className="asm-grid-h">Hierarchy levels</h5>
          <p className="muted asm-help">
            The tiers the chart groups by, outermost first. Each is a column; removing one leaves
            its people under the tier above.
          </p>
          {levels.length === 0 && (
            <p className="muted" data-testid="levels-empty">
              No hierarchy levels — everybody hangs directly under the root.
            </p>
          )}
          {levels.map((l, i) => (
            <div className="field" key={l.key} data-testid={`level-row-${l.key}`}>
              <label htmlFor={`map-${l.key}`}>Level {i + 1}</label>
              <div className="row">
                <input
                  data-testid={`level-label-${l.key}`}
                  value={l.label}
                  disabled={readOnly}
                  aria-label={`Name of level ${i + 1}`}
                  onChange={(e) => relabel(l.key, e.target.value)}
                />
                <select
                  id={`map-${l.key}`}
                  data-testid={`map-${l.key}`}
                  value={draft[l.key] ?? ''}
                  disabled={readOnly}
                  onChange={(e) => set(l.key, e.target.value)}
                >
                  <option value="">— pick a column —</option>
                  {columns.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                <IconButton
                  icon={ICON.moveUp}
                  testId={`level-up-${l.key}`}
                  label={`Move ${l.label} up`}
                  disabled={readOnly || i === 0}
                  onClick={() => moveLevel(i, -1)}
                />
                <IconButton
                  icon={ICON.moveDown}
                  testId={`level-down-${l.key}`}
                  label={`Move ${l.label} down`}
                  disabled={readOnly || i === levels.length - 1}
                  onClick={() => moveLevel(i, 1)}
                />
                <IconButton
                  icon={ICON.remove}
                  testId={`level-remove-${l.key}`}
                  label={`Remove ${l.label}`}
                  disabled={readOnly}
                  onClick={() => removeLevel(l.key)}
                />
              </div>
            </div>
          ))}
          <button data-testid="level-add" disabled={readOnly} onClick={addLevel}>
            ＋ Add a level
          </button>

          {canRebuild && (
            <>
              <h5 className="asm-grid-h">Re-derive the roster</h5>
              <p className="muted asm-help">
                Re-reads every person from the source rows this project retained, under the mapping
                above. Use it after correcting a column that was read wrongly. Decisions, scenarios
                and the change log survive; it is refused if the corrected mapping would change who
                the people are, because that is an import rather than a re-map.
              </p>
              <div className="field">
                <label htmlFor="rebuild-confirm">
                  Type <strong>{projectName}</strong> to confirm
                </label>
                <input
                  id="rebuild-confirm"
                  data-testid="rebuild-confirm"
                  value={confirmName}
                  disabled={readOnly}
                  onChange={(e) => setConfirmName(e.target.value)}
                />
              </div>
              <button
                data-testid="rebuild-roster"
                disabled={readOnly || confirmName !== projectName || dirty}
                title={
                  dirty
                    ? 'Apply the mapping change first — re-deriving reads the mapping the project has, not the one on screen.'
                    : undefined
                }
                onClick={() => {
                  onApply([
                    { type: 'rebuildRosterFromSource', confirmProjectName: projectName },
                  ]);
                  onClose();
                }}
              >
                Re-derive the roster
              </button>
            </>
          )}
        </>
      )}
    </Modal>
  );
}
